import { Input } from 'antd';
import type { ReinsuredTableData } from './types';
import CustomButton from 'components/ui/custom-button';
import { EButtonVariant } from 'components/ui/custom-button/enums';
import CustomTable from 'components/ui/custom-table';
import type { TableColumnsType } from 'antd';
import { AddIcon } from 'assets/icons/add';
import { useNavigate } from 'react-router';
import { ENavigationPath } from 'constants/path';
import { useQuery } from 'react-query';
import usePagination from 'hooks/usePagination';
import { useMemo, useState } from 'react';
import ContentIcon from 'assets/icons/content';
import { reinsuredApi } from 'services/apis';
import { useTranslation } from 'react-i18next';
import SearchIcon from 'assets/icons/search';
import { EQueryKeys } from 'constants/query-keys';

export default function ReinsuredTable() {
  const [inputText, setInputText] = useState('');
  const [searchText, setSearchText] = useState('');
  const { currentPage, pageSize, onPageChange, onShowSizeChange } =
    usePagination();

  const { t } = useTranslation();

  const columns: TableColumnsType<ReinsuredTableData> = useMemo(
    () => [
      {
        key: 'index',
        title: <ContentIcon />,
        dataIndex: 'index',
        width: '1%',
        align: 'center',
        render: (index: number) => index + 1,
      },
      {
        key: 'name',
        title: t('reinsured.reinsuredName'),
        dataIndex: 'name',
        width: '35%',
        align: 'left',
        render: (text: string) => (
          <div
            style={{
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxWidth: '35vw',
            }}
          >
            {text}
          </div>
        ),
      },
      {
        key: 'reinsuredGroup',
        title: t('reinsured.reinsuredGroup'),
        dataIndex: 'reinsuredGroup',
        width: '32%',
        align: 'left',
      },
      {
        key: 'country',
        title: t('general.country'),
        dataIndex: 'country',
        width: '32%',
        align: 'left',
      },
    ],
    [t]
  );

  const {
    data: { items: reinsuredData, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
  } = useQuery(
    [EQueryKeys.REINSURED_LIST, pageSize, currentPage, searchText],
    async () => {
      const { items, pagination } = await reinsuredApi.filterReinsureds({
        page: currentPage,
        pageSize,
        searchText: searchText,
      });
      return {
        items: items.map((reinsured, index) => ({
          index: pageSize * (currentPage - 1) + index,
          key: reinsured.reinsuredId,
          name: reinsured.name,
          reinsuredGroup: reinsured.reinsuredGroup,
          country: reinsured.country,
          status: reinsured.status,
          reinsuredId: reinsured.reinsuredId,
        })),
        pagination,
      };
    }
  );
  const navigate = useNavigate();

  const handleSearchTrigger = () => setSearchText(inputText);

  return (
    <div className="w-full py-4">
      <div className="flex justify-between mb-4 items-center">
        <div className="text-2xl font-medium">
          {t('general.selectField', { field: t('reinsured.reinsured') })}
        </div>
        <CustomButton
          className="w-1/5"
          variant={EButtonVariant.PRIMARY}
          label={t('general.createNewRecord')}
          prefixIcon={<AddIcon />}
          onClick={() => navigate(ENavigationPath.NEW_REINSURED)}
        />
      </div>
      <div className="mb-4 flex items-center gap-2">
        <Input
          placeholder={t('general.search', { field: t('reinsured.reinsured') })}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="flex-grow py-[8px]"
          prefix={<SearchIcon />}
          onPressEnter={handleSearchTrigger}
        />
        <CustomButton
          className="w-2/12 py-[8px]"
          variant={EButtonVariant.PRIMARY}
          label={t('general.search')}
          onClick={handleSearchTrigger}
        />
      </div>

      <CustomTable
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onShowSizeChange={onShowSizeChange}
        columns={columns}
        dataSource={reinsuredData || []}
        total={total || 0}
        onRow={(record) => ({
          onClick: () =>
            navigate(
              ENavigationPath.UPDATE_REINSURED.replace(
                ':id',
                record.reinsuredId
              )
            ),
          className: 'cursor-pointer hover:bg-gray-50',
        })}
      />
    </div>
  );
}
